import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Cable, CheckCircle2, Download, Fingerprint, ShieldCheck, Zap } from 'lucide-react';

const flow = [
  ['01','Connect','Insert the cable and read the plug-resident signed channel record.'],
  ['02','Verify','Compare authenticated identity with physical channel / impedance fingerprint evidence.'],
  ['03','Enable','Sequence power enablement only after the verification state permits it.'],
  ['04','Operate','Carry data and power through one physical assembly while preserving lifecycle evidence.'],
];

export default function GPMIOneCable() {
  const printPage = () => window.print();
  return (
    <main className="min-h-screen bg-[#071426] text-white">
      <Helmet>
        <title>GPMI One Cable | Unified Data + Power Interconnect | SALAR Technologies</title>
        <meta name="description" content="GPMI One Cable is SALAR's patent-pending unified data and power cable architecture with signed channel identity, impedance fingerprint verification and sequenced power enablement." />
        <meta property="og:title" content="GPMI One Cable | SALAR Technologies" />
        <meta property="og:description" content="One physical link. Data + power. Identity + channel evidence. Power only after verification." />
        <meta property="og:url" content="https://www.salartechnologies.com/products/gpmi-one-cable" />
        <meta property="og:type" content="product" />
        <link rel="canonical" href="https://www.salartechnologies.com/products/gpmi-one-cable" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="relative overflow-hidden border-b border-blue-800/30">
        <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage:"linear-gradient(90deg,rgba(7,20,38,.97),rgba(7,20,38,.72),rgba(7,20,38,.90)),url('/img/pexels_fiber_cables.jpg')"}}/>
        <div className="relative mx-auto max-w-6xl px-6 py-20">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-xs font-bold text-emerald-200">PATENT 11 · FILED OCT. 27, 2026</span>
            <span className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1 text-xs font-bold text-cyan-200">U.S. PROVISIONAL 64/185,728</span>
            <span className="rounded-full border border-blue-400/40 bg-blue-400/10 px-3 py-1 text-xs font-bold text-blue-100">ENGINEERING DEVELOPMENT</span>
          </div>
          <p className="mt-7 text-xs font-bold uppercase tracking-[.3em] text-cyan-300">SALAR PHYSICAL INTERCONNECT PRODUCT</p>
          <h1 className="mt-3 max-w-4xl text-5xl font-bold leading-[1.05] md:text-7xl">GPMI One Cable</h1>
          <p className="mt-6 max-w-4xl text-xl leading-8 text-blue-100">One physical link. Data + power. Identity + channel evidence. <strong className="text-white">Power only after verification.</strong></p>
          <p className="mt-5 max-w-3xl leading-7 text-blue-200">
            A patent-pending cable architecture that moves trust into the physical assembly: the plug carries a signed channel record,
            the receiving system can verify physical-channel fingerprint evidence, and power enablement becomes a controlled sequence rather than an unconditional first step.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={printPage} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-5 py-3 font-bold text-[#071426] hover:bg-cyan-300">
              <Download size={17}/> Print / Save Product Sheet
            </button>
            <Link to="/patent-portfolio#patent-11" className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/40 bg-cyan-400/10 px-5 py-3 font-bold text-cyan-100 hover:bg-cyan-400/20">
              View Patent 11 <ArrowRight size={17}/>
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              [ShieldCheck,'Plug-resident signed channel record','Authenticated assembly identity and channel record can travel with the physical cable rather than living only in an external asset database.'],
              [Fingerprint,'Impedance fingerprint verification','Physical electrical characteristics can participate in a pre-power verification decision, helping distinguish expected assemblies from suspect, damaged or mismatched conditions.'],
              [Zap,'Sequenced power enablement','Power delivery can be staged and gated by verification state, connecting identity, physical-channel evidence and the enablement decision in one architecture.'],
            ].map(([Icon,h,b]) => (
              <article key={h} className="rounded-2xl border border-blue-700/30 bg-[#0D203B] p-6">
                <Icon className="text-cyan-300" size={28}/>
                <h2 className="mt-4 text-xl font-bold">{h}</h2>
                <p className="mt-3 leading-7 text-blue-100">{b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-blue-800/30 bg-[#0A1628] px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[.28em] text-cyan-300">PUBLIC-SAFE OPERATING FLOW</p>
          <h2 className="mt-3 text-3xl font-bold">From cable insertion to controlled power-up</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-4">
            {flow.map(([n,h,b]) => (
              <div key={n} className="relative rounded-xl border border-blue-700/30 bg-[#0D203B] p-5">
                <div className="text-4xl font-black text-cyan-400/35">{n}</div>
                <h3 className="mt-3 text-lg font-bold">{h}</h3>
                <p className="mt-2 text-sm leading-6 text-blue-200">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-blue-700/30 bg-[#0D203B] p-7">
            <p className="text-xs font-bold uppercase tracking-[.24em] text-blue-300">PHYSICAL PRODUCT, NOT A SOFTWARE APP</p>
            <h2 className="mt-3 text-2xl font-bold">Intelligence is designed into the cable / plug architecture.</h2>
            <p className="mt-4 leading-7 text-blue-100">
              The core function is a physical interconnect and power-enable architecture. It does not require a separate customer-facing software product to be meaningful.
              Host firmware, management or lifecycle tooling may consume evidence, but the inventive center is the cable assembly, its signed channel identity, physical verification and controlled power sequence.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-700/30 bg-[#0D203B] p-7">
            <p className="text-xs font-bold uppercase tracking-[.24em] text-blue-300">WHERE IT CAN MATTER</p>
            <div className="mt-4 space-y-3">
              {['AI / HPC and data-center interconnects','High-bandwidth display and media systems','Industrial and robotic platforms','Defense / field electronics requiring controlled power-up and traceability'].map(x => (
                <div key={x} className="flex gap-3 text-blue-100"><CheckCircle2 className="mt-1 shrink-0 text-emerald-300" size={17}/><span>{x}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl border border-cyan-400/25 bg-[#071225] p-7">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_.85fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.24em] text-cyan-300">ILLUSTRATIVE SIGNAL-INTEGRITY VIEW</p>
              <svg viewBox="0 0 700 220" className="mt-5 w-full" role="img" aria-label="Illustrative eye diagram concept, not measured product data">
                <rect width="700" height="220" rx="18" fill="#071225"/>
                {[0,1,2,3].map(i => <path key={i} d={`M30 ${110+(i-1.5)*8} C190 ${20+i*8}, 270 ${20+i*8}, 350 110 C430 ${200-i*8}, 510 ${200-i*8}, 670 ${110+(i-1.5)*8}`} fill="none" stroke="#00D4FF" strokeOpacity={0.45} strokeWidth="2"/>)}
                {[0,1,2,3].map(i => <path key={'b'+i} d={`M30 ${110+(i-1.5)*8} C190 ${200-i*8}, 270 ${200-i*8}, 350 110 C430 ${20+i*8}, 510 ${20+i*8}, 670 ${110+(i-1.5)*8}`} fill="none" stroke="#1B6EC2" strokeOpacity={0.55} strokeWidth="2"/>)}
              </svg>
              <p className="mt-2 text-xs text-blue-400">Illustrative concept only — not measured eye-mask, BER or compliance data.</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[.24em] text-[#D4A843]">VALIDATION-GATED RELEASE</p>
              <h2 className="mt-3 text-2xl font-bold">No invented specifications.</h2>
              <p className="mt-4 leading-7 text-blue-100">
                Final connector geometry, data rate, power-delivery level, reach, impedance tolerance, eye-mask margin, BER/FEC behavior, thermal limits,
                safety certification and interoperability claims remain controlled prototype / standards-validation outputs.
              </p>
              <div className="mt-6 rounded-xl border-l-4 border-[#D4A843] bg-[#112844] p-4 text-sm leading-6 text-blue-100">
                <strong className="text-white">GPMI naming boundary.</strong> GPMI is an industry interface reference. SALAR’s GPMI One Cable is an independent patent-pending product direction and does not claim GPMI certification, consortium endorsement or wire-level compatibility unless separately established.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1628] px-6 py-14">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[.24em] text-cyan-300">PATENT 11</p>
          <h2 className="mt-3 max-w-5xl text-2xl font-bold">Unified Data and Power Cable Assembly with Plug-Resident Signed Channel Record, Impedance Fingerprint Verification, and Sequenced Power Enablement</h2>
          <p className="mt-4 text-blue-200">U.S. Provisional Application No. 64/185,728 · Docket SALAR-OCABLE-001 · Filed October 27, 2026 · First named inventor: Vikrant Salar · Patent Pending</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/news-updates" className="inline-flex items-center gap-2 rounded-lg border border-blue-500/40 px-4 py-2 text-blue-100 hover:bg-blue-500/10">News & Updates <ArrowRight size={15}/></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-lg border border-blue-500/40 px-4 py-2 text-blue-100 hover:bg-blue-500/10">Product / partner inquiry <ArrowRight size={15}/></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
